import { useSubscriptionStore } from '@easy-expense/data-firestore-client';
import React, { JSX } from 'react';
import { useNavigate } from 'react-router-dom';

import { MeteredPaywallActions, useHandleMeteredAction } from '../hooks/useHandleMeteredAction';
import { useDisablePaywall } from '../lib/disablePaywall';

/** How long to wait for the workspace coverage check before deciding with what is cached. */
const COVERAGE_WAIT_MS = 10000;

/**
 * Route gate beside RequireManager: renders the page when the plan covers the action, otherwise
 * shows the upgrade modal over an empty page and returns the user to transactions when it closes.
 *
 * Holds until the session's workspace coverage check has finished (or the wait runs out), so a
 * fresh browser does not judge a subscriber before their coverage has been fetched. With staging's
 * Disable Paywall on, the page renders at once; turning it off decides the visit from there.
 */
export const RequirePlan = ({
  action,
  children,
}: {
  action: MeteredPaywallActions;
  children: JSX.Element;
}) => {
  const navigate = useNavigate();
  const coverageChecked = useSubscriptionStore((s) => s.coverageChecked);
  const [ready, setReady] = React.useState(coverageChecked);
  const [allowed, setAllowed] = React.useState(false);
  const decided = React.useRef(false);
  const disablePaywall = useDisablePaywall();
  const handleMeteredAction = useHandleMeteredAction(action, {
    onPaywallDismissed: () => navigate('/transactions', { replace: true }),
  });

  React.useEffect(() => {
    if (coverageChecked) {
      setReady(true);
      return;
    }
    const timeout = setTimeout(() => setReady(true), COVERAGE_WAIT_MS);
    return () => clearTimeout(timeout);
  }, [coverageChecked]);

  React.useEffect(() => {
    // While Disable Paywall is on the page is let in, and the visit is left undecided so turning
    // it off decides afresh. The page stays mounted through that decision unless it walls.
    if (disablePaywall) {
      decided.current = false;
      setAllowed(true);
      return;
    }
    // Decided once per visit: re-running on every store tick would reopen the modal the user
    // just dismissed.
    if (decided.current) {
      return;
    }
    // Disable Paywall turned off before coverage is known: hold the page as a fresh visit would.
    if (!ready) {
      setAllowed(false);
      return;
    }
    decided.current = true;
    handleMeteredAction(
      () => setAllowed(true),
      () => setAllowed(false),
    );
  }, [ready, disablePaywall]);

  if (!allowed && !disablePaywall) {
    return null;
  }
  return children;
};
